import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

// @mui material components
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

function UserMenu({ anchorEl, onClose, employee, viewAsAdmin, switchView, checkLoggedInStatus }) {
  const isMenuOpen = Boolean(anchorEl);

  // Logic to revert from Ghost Mode back to Super Admin
  const handleSwitchBackToSuperAdmin = () => {
    const originalToken = localStorage.getItem("originalToken");

    if (originalToken) {
      // 1. Restore the original Super Admin Token
      localStorage.setItem("authToken", originalToken);

      // 2. Reset Tenant to empty (Super Admin context)
      localStorage.setItem("tenant", "");

      // 3. cleanup flags
      localStorage.removeItem("isSuperAdminSession");
      localStorage.removeItem("originalToken");

      // 4. Update Global Auth Context
      if (checkLoggedInStatus) {
        checkLoggedInStatus();
      }

      // 5. Close Menu
      onClose();
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={isMenuOpen}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        sx: {
          mt: 1.5,
          borderRadius: "16px",
          minWidth: "220px",
          boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
          padding: "8px",
        },
      }}
    >
      {/* HEADER: Avatar & Name */}
      <MDBox px={2} py={1.5} display="flex" alignItems="center" gap={1.5}>
        <Avatar
          src={employee?.photo ? `data:image/jpeg;base64,${employee.photo}` : null}
          sx={{ width: 48, height: 48 }}
        />
        <MDBox>
          <Typography variant="subtitle2" fontWeight="bold">
            {employee?.name || "User"}
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              display: "block",
              lineHeight: 1,
              fontWeight: "medium",
              textTransform: "capitalize",
            }}
          >
            {employee?.jobTitle || employee?.departmentName || "Active Session"}
          </Typography>
        </MDBox>
      </MDBox>

      <Divider sx={{ my: 1 }} />

      {/* ITEM 1: Profile */}
      <MenuItem
        component={Link}
        to="/profile"
        onClick={onClose}
        sx={{
          borderRadius: "12px",
          mx: 1,
          justifyContent: "center",
          backgroundColor: "#f8f9fa",
          fontWeight: "bold",
          fontSize: "0.85rem",
        }}
      >
        My Profile
      </MenuItem>

      {/* ITEM 2: Super Admin Panel (Direct Access) */}
      {/* Logic: Tenant is empty string AND Auth Token exists */}
      {localStorage.getItem("tenant") === "" && localStorage.getItem("authToken") && (
        <MenuItem
          component={Link}
          to="/super-admin"
          onClick={onClose}
          sx={{
            borderRadius: "12px",
            mx: 1,
            mt: 1,
            fontSize: "0.85rem",
            color: "#344767",
            "&:hover": { backgroundColor: "#f0f2f5" },
          }}
        >
          <Icon sx={{ mr: 1, fontSize: "18px !important", color: "#1A73E8" }}>
            admin_panel_settings
          </Icon>
          Super Admin Panel
        </MenuItem>
      )}

      {/* ITEM 3: Switch BACK to Super Admin (Ghost Mode Exit) */}
      {localStorage.getItem("isSuperAdminSession") === "true" &&
        localStorage.getItem("originalToken") && (
          <MenuItem
            component={Link}
            to="/super-admin"
            onClick={handleSwitchBackToSuperAdmin}
            sx={{
              borderRadius: "12px",
              mx: 1,
              mt: 1,
              fontSize: "0.85rem",
              color: "#344767",
              "&:hover": { backgroundColor: "#f0f2f5" },
            }}
          >
            <Icon sx={{ mr: 1, fontSize: "18px !important", color: "#1A73E8" }}>
              manage_accounts
            </Icon>
            Switch back to Super Admin
          </MenuItem>
        )}

      {/* ITEM 4: Local Admin/User View Switcher */}
      {employee?.role === 1 && (
        <MenuItem
          onClick={() => {
            switchView();
            onClose();
          }}
          sx={{ borderRadius: "12px", mx: 1, fontSize: "0.85rem", mt: 1 }}
        >
          <Icon sx={{ mr: 1, fontSize: "18px !important" }}>swap_horiz</Icon>
          {viewAsAdmin ? "Switch to User View" : "Switch to Admin View"}
        </MenuItem>
      )}
    </Menu>
  );
}

// Typechecking
UserMenu.propTypes = {
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  onClose: PropTypes.func.isRequired,
  employee: PropTypes.shape({
    name: PropTypes.string,
    photo: PropTypes.string,
    jobTitle: PropTypes.string,
    departmentName: PropTypes.string,
    role: PropTypes.number,
  }),
  viewAsAdmin: PropTypes.bool.isRequired,
  switchView: PropTypes.func.isRequired,
  checkLoggedInStatus: PropTypes.func, // Optional, but recommended for Ghost Mode
};

UserMenu.defaultProps = {
  anchorEl: null,
  employee: {},
  checkLoggedInStatus: () => {},
};

export default UserMenu;
